/**
 * App Router
 * 
 * @file client/src/App.jsx
 * @description Main application router with public and admin routes
 * @project Labs.ChatWithDr
 */

import { Routes, Route } from 'react-router-dom'
import { lazy, Suspense, useEffect } from 'react'
import { trackVisit } from './utils/visitorTracker'

// Layout
import MainLayout from './components/layout/MainLayout'
import AdminLayout from './components/layout/AdminLayout'

// Loading Component
import Loading from './components/common/Loading'
import ProtectedRoute from './components/common/ProtectedRoute'

// Eager load main pages
import Home from './pages/Home'
import Tests from './pages/Tests'

// Lazy load other pages
const TestDetail = lazy(() => import('./pages/TestDetail'))
const Checkout = lazy(() => import('./pages/Checkout'))
const Confirmation = lazy(() => import('./pages/Confirmation'))
const HowItWorks = lazy(() => import('./pages/HowItWorks'))
const About = lazy(() => import('./pages/About'))
const FAQ = lazy(() => import('./pages/FAQ'))
const Contact = lazy(() => import('./pages/Contact'))
const Privacy = lazy(() => import('./pages/Privacy'))
const Terms = lazy(() => import('./pages/Terms'))
const Blog = lazy(() => import('./pages/Blog'))
const BlogPost = lazy(() => import('./pages/BlogPost'))

// Admin pages
const AdminLogin = lazy(() => import('./pages/admin/Login'))
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'))
const AdminOrders = lazy(() => import('./pages/admin/Orders'))
const AdminOrderDetail = lazy(() => import('./pages/admin/OrderDetail'))
const AdminTests = lazy(() => import('./pages/admin/Tests'))
const AdminCategories = lazy(() => import('./pages/admin/Categories'))
const AdminUsers = lazy(() => import('./pages/admin/Users'))
const AdminContacts = lazy(() => import('./pages/admin/Contacts'))
const AdminSubscribers = lazy(() => import('./pages/admin/Subscribers'))
const AdminSettings = lazy(() => import('./pages/admin/Settings'))
const AdminLabRequisition = lazy(() => import('./pages/admin/LabRequisition'))
const AdminLabResults = lazy(() => import('./pages/admin/LabResults'))
const AdminBlogManager = lazy(() => import('./pages/admin/BlogManager'))
const AdminBlogEditor = lazy(() => import('./pages/admin/BlogEditor'))
const AdminWebSource = lazy(() => import('./pages/admin/WebSource'))
const AdminBookingSource = lazy(() => import('./pages/admin/BookingSource'))

function App() {
  // Track visitor source on every page load
  useEffect(() => {
    trackVisit()
  }, [])

  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<MainLayout />}>
          <Route index element={<Home />} />
          <Route path="tests" element={<Tests />} />
          <Route path="tests/:slug" element={<TestDetail />} />
          <Route path="checkout" element={<Checkout />} />
          <Route path="confirmation/:orderNumber" element={<Confirmation />} />
          <Route path="how-it-works" element={<HowItWorks />} />
          <Route path="about" element={<About />} />
          <Route path="faq" element={<FAQ />} />
          <Route path="contact" element={<Contact />} />
          <Route path="privacy" element={<Privacy />} />
          <Route path="terms" element={<Terms />} />
          <Route path="blog" element={<Blog />} />
          <Route path="blog/:slug" element={<BlogPost />} />
        </Route>

        {/* Admin Routes - Custom URL for security */}
        <Route path="/labsdrnileshpanchal/login" element={<AdminLogin />} />
        <Route path="/labsdrnileshpanchal" element={<AdminLayout />}>
          <Route index element={<ProtectedRoute permission="dashboard"><AdminDashboard /></ProtectedRoute>} />
          <Route path="orders" element={<ProtectedRoute permission="orders"><AdminOrders /></ProtectedRoute>} />
          <Route path="orders/:id" element={<ProtectedRoute permission="orders"><AdminOrderDetail /></ProtectedRoute>} />
          <Route path="lab-requisition" element={<ProtectedRoute permission="orders"><AdminLabRequisition /></ProtectedRoute>} />
          <Route path="lab-results" element={<ProtectedRoute permission="orders"><AdminLabResults /></ProtectedRoute>} />
          <Route path="tests" element={<ProtectedRoute permission="tests"><AdminTests /></ProtectedRoute>} />
          <Route path="categories" element={<ProtectedRoute permission="categories"><AdminCategories /></ProtectedRoute>} />
          <Route path="users" element={<ProtectedRoute permission="users"><AdminUsers /></ProtectedRoute>} />
          <Route path="contacts" element={<ProtectedRoute permission="contacts"><AdminContacts /></ProtectedRoute>} />
          <Route path="subscribers" element={<ProtectedRoute permission="contacts"><AdminSubscribers /></ProtectedRoute>} />
          <Route path="settings" element={<ProtectedRoute permission="settings"><AdminSettings /></ProtectedRoute>} />
          <Route path="blog" element={<ProtectedRoute permission="blog"><AdminBlogManager /></ProtectedRoute>} />
          <Route path="blog/new" element={<ProtectedRoute permission="blog"><AdminBlogEditor /></ProtectedRoute>} />
          <Route path="blog/edit/:id" element={<ProtectedRoute permission="blog"><AdminBlogEditor /></ProtectedRoute>} />
          <Route path="web-source" element={<ProtectedRoute permission="dashboard"><AdminWebSource /></ProtectedRoute>} />
          <Route path="booking-source" element={<ProtectedRoute permission="dashboard"><AdminBookingSource /></ProtectedRoute>} />
        </Route>
      </Routes>
    </Suspense>
  )
}

export default App